import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  NgZone,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-services-section',
  templateUrl: './services-section.component.html',
  styleUrls: ['./services-section.component.css'],
})
export class ServicesSectionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('carouselRef') carouselRef!: ElementRef;

  currentIndex: number = 0;
  totalCards: number = 6;
  cardsToShow: number = 3;
  translateX: number = 0;
  isMobile: boolean = false;

  // Single source of truth: how many px to shift per step
  private stepSize: number = 0;
  private readonly DESKTOP_GAP = 25; // must match CSS `gap` on .carousel-track

  // .services-section has padding: 30px 20px on mobile → 20px × 2 = 40px horizontal
  // Update this if you ever change the CSS padding.
  private readonly MOBILE_SECTION_PADDING = 40;

  private resizeObserver!: ResizeObserver;

  private touchStartX = 0;
  private touchEndX = 0;
  private readonly SWIPE_THRESHOLD = 50;

  get dir(): 'rtl' | 'ltr' {
    return this.translate.currentLang === 'ar' ? 'rtl' : 'ltr';
  }

  get totalCardsArray(): number[] {
    return Array.from({ length: this.totalCards }, (_, i) => i);
  }

  constructor(
    private translate: TranslateService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // Set the flag immediately so the template renders correct classes
    // before the view is painted (avoids a flash of wrong layout).
    this.isMobile = window.innerWidth < 768;
    this.cardsToShow = this.isMobile ? 1 : this.getDesktopCardsToShow();
  }

  ngAfterViewInit(): void {
    // ResizeObserver fires as soon as the element has a size (first paint)
    // AND every time it changes (orientation flip, DevTools resize, etc.).
    // This completely replaces the fragile setTimeout + window resize combo.
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        // Re-enter Angular zone so change detection picks up the new translateX
        this.ngZone.run(() => this.recalculate());
      });
      this.resizeObserver.observe(this.carouselRef.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // Keep as backup for environments where ResizeObserver misses window resizes
  @HostListener('window:resize')
  onWindowResize(): void {
    this.recalculate();
  }

  private recalculate(): void {
    const windowWidth = window.innerWidth;
    this.isMobile = windowWidth < 768;

    if (this.isMobile) {
      this.cardsToShow = 1;

      // ✅ Read the carousel element's true rendered pixel width.
      //    offsetWidth already accounts for every ancestor's padding/margin,
      //    so there is no guessing or magic number subtraction needed.
      const carouselWidth = this.carouselRef?.nativeElement?.offsetWidth ?? 0;

      // Fallback: use window width minus the known section padding.
      // Only needed if the element hasn't painted yet (extremely rare).
      this.stepSize =
        carouselWidth > 0
          ? carouselWidth
          : windowWidth - this.MOBILE_SECTION_PADDING;
    } else {
      this.cardsToShow = this.getDesktopCardsToShow();
      // ✅ Add the CSS gap so each step lands exactly on the next card's left edge.
      this.stepSize = this.getDesktopCardWidth() + this.DESKTOP_GAP;
    }

    // Clamp the index so it stays valid after a resize changes cardsToShow
    const maxIndex = Math.max(0, this.totalCards - this.cardsToShow);
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }

    this.updateTranslateX();
  }

  private getDesktopCardsToShow(): number {
    const w = window.innerWidth;
    if (w < 992) return 2;
    return 3;
  }

  private getDesktopCardWidth(): number {
    const w = window.innerWidth;
    if (w < 992) return 285;
    if (w < 1200) return 305;
    return 325;
  }

  private updateTranslateX(): void {
    this.translateX = -this.currentIndex * this.stepSize;
  }

  nextSlide(): void {
    if (this.currentIndex < this.totalCards - this.cardsToShow) {
      this.currentIndex++;
      this.updateTranslateX();
    }
  }

  prevSlide(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateTranslateX();
    }
  }

  goToSlide(index: number): void {
    this.currentIndex = index;
    this.updateTranslateX();
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const delta = this.touchStartX - this.touchEndX;
    if (Math.abs(delta) < this.SWIPE_THRESHOLD) return;
    delta > 0 ? this.nextSlide() : this.prevSlide();
  }
}
